import { useState, useCallback } from 'react';
import { QueryKey } from 'react-query';

import { OperationType } from '../../../../../../../main/operations/operationsTypes';

import {
  UniversalFilterOperationTypeValue,
  UniversalFilterOperationTypeModes,
  UniversalFilterOperationTypeFilterState,
  UniversalFilterOperationTypeOnFilterStateChange,
  UniversalFilterOperationTypeOnChange
} from '../../UniversalFilterOperationType.types';

import { useMountEffect } from '../../../../../../../common/hooks/useMountEffect';
import { useReactQueryState } from '../../../../../../../main/common/reactQuery/hooks/useReactQueryState';

import { useUniversalFilterOperationTypeFilterState } from '../useUniversalFilterOperationTypeFilterState';

import {
  UniversalFilterTemplateModeOption,
  UniversalFilterTemplateOnModeChange
} from '../../../../helpers/UniversalFilterTemplate';

import { filtersKeys } from '../../../../../../../locales/keys';

const defaultMode = UniversalFilterOperationTypeModes.IS_ANY_OF;

const modeOptions: UniversalFilterTemplateModeOption<UniversalFilterOperationTypeModes>[] =
  [
    {
      value: UniversalFilterOperationTypeModes.IS_ANY_OF,
      i18nText: filtersKeys.modes.isAnyOf
    },
    {
      value: UniversalFilterOperationTypeModes.IS_NONE_OF,
      i18nText: filtersKeys.modes.isNoneOf
    }
  ];

interface UniversalFilterOperationTypeOptions {
  filterStateCacheKey: QueryKey;
  filterValue?: UniversalFilterOperationTypeValue;
  onChange: UniversalFilterOperationTypeOnChange;
}

function useUniversalFilterOperationType({
  filterStateCacheKey,
  filterValue,
  onChange
}: UniversalFilterOperationTypeOptions) {
  const { value: savedState, setValue: saveState } =
    useReactQueryState<UniversalFilterOperationTypeFilterState>(
      filterStateCacheKey
    );

  const { handleApplyFilterState, handleParseFilterValue } =
    useUniversalFilterOperationTypeFilterState({
      onChange
    });

  const [filterState, setFilterState] =
    useState<UniversalFilterOperationTypeFilterState>(
      savedState ||
        handleParseFilterValue(filterValue) || {
          mode: defaultMode,
          states: []
        }
    );

  useMountEffect(() => {
    handleApplyFilterState(filterState);
  });

  const handleFilterStateChange =
    useCallback<UniversalFilterOperationTypeOnFilterStateChange>(
      (state) => {
        setFilterState(state);
        handleApplyFilterState(state);
        saveState(state);
      },
      [handleApplyFilterState, saveState]
    );

  const handleModeChange = useCallback<
    UniversalFilterTemplateOnModeChange<UniversalFilterOperationTypeModes>
  >(
    (mode) => {
      handleFilterStateChange({
        mode,
        states: filterState.states || []
      });
    },
    [filterState, handleFilterStateChange]
  );

  const handleStatusesChange = useCallback<(states: OperationType[]) => void>(
    (states) => {
      handleFilterStateChange({
        mode: filterState.mode,
        states: states || []
      });
    },
    [filterState, handleFilterStateChange]
  );

  return {
    modeOptions,
    filterState,
    handleModeChange,
    handleStatusesChange
  };
}

export default useUniversalFilterOperationType;
